import {
  SET_COMPANY_LIST,
  SET_COMPANY_LIST_MAP,
  SET_COMPANY_LIST_FETCHING,
  SET_NEW_ISSUES_LIST,
  ADD_TO_COMPANY_LIST,
  SET_COMPANY_WATCHER,
  CURRENT_COMPANY,
  UPDATE_CURRENT_COMPANY,
  SET_GICS_LIST,
  SET_CASH_SOURCE_LIST,
  TERMINAL,
  GET_UI_INFO,
  SET_HISTORY_LIST,
  ADD_HISTORY_ENTRY,
  SET_MOST_VIEWED,
  SET_NEXT_REPORTING_LIST,
  SET_LAST_UPDATED_LIST,
  SET_COMPANY_COUNT,
  SET_SEARCH_FIELD,
  SET_SORTING,
  SET_MEMO_STRUCTURE_LIST,
  SET_COMPANY_LIST_ERROR,
  SET_COMPANY_LIST_PAGE,
  SET_COMPANY_RECENT_DOCUMENTS,
  SET_SEARCH_SUGGESTIONS,
  SET_RAC_FILTER,
  SET_EARNINGS_SUMMARY,
  SET_WATCHLIST_TOOLTIP,
  ADD_TO_WATCHLIST,
  REMOVE_FROM_WATCHLIST,
} from './mutations'

// Libs
import gQLMixin, { handleGraphQLErr } from '@/mixins/graphqlMixin.js'
import Auth from '@aws-amplify/auth'
import {
  instance,
  admin,
  getCompaniesTimeseries as getCompaniesTimeseriesApi,
  getInstrumentsTimeseries as getInstrumentsTimeseriesApi,
  getCompanyCompsList,
} from '@cognitivecredit/ui-utils'

// Graph QL
import * as gqlQuery from './queries.graphql'
import * as gqlMutation from './mutations.graphql'
import * as gqlSubscription from './subscriptions.graphql'
import { useGraphQL } from '@/composables/graphQL'
import { ActionContext } from 'vuex'
import { useWatchlistsStore } from '@/piniaStores/watchlists/watchlistsStore'
import { storeToRefs } from 'pinia'
import { CompanyState } from '@/stores/Company/state'

const companyActions = {
  toggleTerminal({ commit }: ActionContext, payload) {
    commit(TERMINAL, payload)
  },
  toggleNewIssuesList({ commit }: ActionContext, payload) {
    commit(SET_NEW_ISSUES_LIST, payload || null)
  },
  toggleRACListFilter({ commit }: ActionContext, { type, value }) {
    commit(SET_RAC_FILTER, { type, value })
  },
  setCompanySorting({ commit }: ActionContext, payload) {
    commit(SET_SORTING, payload)
  },
  async getClosestSeenDate({ dispatch }: ActionContext) {
    const lastSeenDate = await dispatch('Notification/getLastSeenNotification', {}, { root: true })

    if (lastSeenDate.length > 0) {
      const timeStampList = lastSeenDate.map((item) => new Date(JSON.parse(item.data).dateTime).getTime())
      return lastSeenDate[timeStampList.indexOf(Math.max(...timeStampList))]
    }

    return null
  },
  async listenCompanyChanges({ commit, dispatch, state }: ActionContext) {
    const session = await Auth.currentSession()
    const currentUserCompanyId = session.idToken.payload['custom:clientId']

    if (!state.companyWatcher) {
      commit(SET_COMPANY_WATCHER)
      const closestSeenDate = await dispatch('getClosestSeenDate')

      if (closestSeenDate) {
        await dispatch('Notification/getMissedNotifications', JSON.parse(closestSeenDate.data).dateTime, { root: true })
      }

      const subscriptionObject = await gQLMixin.methods.subscribeToData(
        gqlSubscription.CompanyNotifications,
        async (res) => {
          if (res?.value?.data?.companyNotifications) {
            const closestSeenDate = await dispatch('getClosestSeenDate')

            dispatch(
              'Notification/newNotification',
              {
                id: closestSeenDate ? closestSeenDate.id : res.value.data.companyNotifications.dateTime,
                data: res.value.data.companyNotifications,
              },
              { root: true },
            )
          }
        },
        { clientId: currentUserCompanyId },
        () => {
          commit(SET_COMPANY_WATCHER, false)
        },
      )

      return subscriptionObject
    }
  },
  async setSearchParam({ commit }: ActionContext, payload?) {
    commit(SET_SEARCH_FIELD, payload)
  },
  /**
   * @param { Object } payload
   * @param { Object } payload.filter e.g. { licenseId: Array, }
   * @param { Object } payload.nextReportingFilter e.g. { licenseId: Array, retired: Boolean, nextReportingIsInThePast: Boolean }
   */
  async initCompanyList({ commit }: ActionContext, payload) {
    try {
      commit(SET_COMPANY_LIST_FETCHING, true)
      const { response } = await useGraphQL(gqlQuery.GetInitialCompanyList, payload)
      const { data } = response
      if (data?.companyList?.data && data.nextReportingList?.data) {
        commit(SET_COMPANY_LIST, data?.companyList?.data)
        commit(SET_COMPANY_COUNT, data?.companyList?.metadata)
        commit(SET_NEXT_REPORTING_LIST, data?.nextReportingList?.data)
        return data
      }
    } catch (err) {
      handleGraphQLErr(err)
      return err
    } finally {
      commit(SET_COMPANY_LIST_FETCHING, false)
    }
  },
  async getAllCompanyListOrderedByNextReporting(_, payload) {
    payload.limit = 2000
    payload.filters.retired = false
    const companies = {}
    const res = await gQLMixin.methods.communicate(gqlQuery.GetCompanyListOrderedByNextReporting, payload)
    const theActualData = res.data.getCompanyListV2.data
    theActualData.forEach((company, index) => {
      companies[company.name] = {
        ...company,
        sortKey: index,
      }
    })
    return companies
  },
  async getCompanyList({ commit, state }: ActionContext, payload) {
    if (state.companyListError) {
      // Reset the error state on fetch
      commit(SET_COMPANY_LIST_ERROR)
    }

    try {
      const { data } = await gQLMixin.methods.communicate(gqlQuery.GetCompanyList, payload)
      if (state.companyList.length > 0 && payload.offset > 0) {
        commit(ADD_TO_COMPANY_LIST, data.companyList.data)
      } else {
        commit(SET_COMPANY_LIST_ERROR, data.companyList.error)
        commit(SET_COMPANY_LIST, data.companyList.data ?? [])
        commit(SET_COMPANY_COUNT, data.companyList.metadata ?? {})
      }
      return data
    } catch (err) {
      handleGraphQLErr(err)
      return err
    }
  },
  async getCompanyMap({ commit }: ActionContext) {
    try {
      const { data } = await gQLMixin.methods.communicate(gqlQuery.GetCompanyListMap, {})
      commit(SET_COMPANY_LIST_MAP, data.getCompanyList)

      return data.getCompanyList
    } catch (err) {
      handleGraphQLErr(err)
      return err
    }
  },
  async getHistoryList({ commit }: ActionContext) {
    try {
      const { data } = await gQLMixin.methods.communicate(gqlQuery.GetHistoryList)

      commit(SET_HISTORY_LIST, data.listFlexibleUserStorageCollection.items)
      return data.listFlexibleUserStorageCollection.items
    } catch (err) {
      handleGraphQLErr(err)
    }
  },
  async setHistoryEntry({ commit, state }: ActionContext, payload) {
    if (!payload || !state.currentCompany) {
      return
    }

    const { id, name, ticker } = state.currentCompany
    const getPathDuplicate = payload.path.split(`${id}/`).pop().split('/')

    if (getPathDuplicate.length > 1) {
      return
    }

    try {
      const entry = {
        id,
        name,
        ticker,
        path: payload.path,
      }

      const { data } = await gQLMixin.methods.communicate(gqlMutation.AddHistoryItem, { data: JSON.stringify(entry) })
      if (data.createFlexibleUserStorageItemV2) {
        commit(ADD_HISTORY_ENTRY, data.createFlexibleUserStorageItemV2.data)
      }
    } catch (err) {
      handleGraphQLErr(err)
    }
  },
  /**
   * @param { String } search The keyword
   * @param { Boolean } noLicenseAware if the search should apply to all the licenses available
   */
  async getSearchSuggestions({ state, commit }: ActionContext<CompanyState>, payload) {
    const opts = { search: payload?.keyword ?? '', filter: {} }

    if (!payload.noLicenseAware) {
      opts.filter.licenseId = state.selectedUniverseIds
    }

    if (payload.excludeGICs) {
      opts.filter.excludeGICs = payload.excludeGICs
    }
    if (payload.isTextSearch) {
      opts.textSearchMode = true
    }

    try {
      const { response } = await useGraphQL(gqlQuery.GetSearchSuggestions, opts)
      if (response.data.suggestionList) {
        commit(SET_SEARCH_SUGGESTIONS, response.data.suggestionList)
        return response.data.suggestionList
      }
    } catch (e) {
      const err = e as any
      console.error('[ERROR] Getting search suggestions', err)
      return err.data.suggestionList
    }
  },
  async setSearchSuggestions({ commit }: ActionContext, payload) {
    commit(SET_SEARCH_SUGGESTIONS, payload)
  },
  /**
   * @param { String } payload The search keyword
   */
  async getDiffCompanySuggestions(unused, payload) {
    let endpoint = '/diff_companies_list?'

    if (payload.batchA) {
      endpoint += `batch_id_1=${payload.batchA}`
    }

    if (payload.batchB) {
      endpoint += `&batch_id_2=${payload.batchB}`
    }

    if (payload.search) {
      endpoint += `&search=${payload.search}`
    }

    try {
      const request = await admin()
      const response = await request.get(endpoint)

      const { data, error } = response

      if (error) {
        throw new Error(error)
      }

      return data
    } catch (err) {
      console.error('[ERROR] Getting company search suggestions', err)
      return err
    }
  },
  /**
   *
   * @param {*} unused
   * @param {Object} payload can contain `search` or `ticker`
   * @returns list of suggestions or error
   */
  async getAdminCompanySuggestions(_unused, payload = {}) {
    try {
      const { response } = await useGraphQL(gqlQuery.GetCompanySuggestions, payload)
      return response.data?.suggestionList
    } catch (err) {
      console.error('[ERROR] Getting admin company search suggestions', err)
      return err
    }
  },
  async getInstrumentChart(_unused, payload) {
    try {
      const { data } = await gQLMixin.methods.communicate(gqlQuery.GetInstrumentChart, payload ? payload : {})
      return data.getInstrumentGraph
    } catch (err) {
      console.error(err)
      return err.data.getInstrumentGraph
    }
  },
  async getInstrumentsTimeseries(_unused, payload) {
    if (!payload) {
      return []
    }

    try {
      const { data } = await getInstrumentsTimeseriesApi({
        series: payload.series,
        instruments: payload.instruments,
        range: payload.range,
      })
      return data
    } catch (err) {
      console.error('[ERROR] Fetching time series', err)
      return null
    }
  },
  async getCompaniesTimeseries(unused, payload) {
    if (!payload?.tag) {
      return {}
    }

    try {
      const { data } = await getCompaniesTimeseriesApi({
        sheetType: payload.sheetType,
        tag: payload.tag,
        companies: payload.companies,
        interval: payload.interval,
      })
      return data
    } catch (err) {
      console.error('[ERROR] Fetching time series', err)
      return null
    }
  },
  async getMostViewedCompanies({ commit, rootGetters }, limit = 5) {
    try {
      const licenses = rootGetters['User/selectedUniverseIds']
      const filter = {
        limit,
        licenseIds: licenses,
      }
      const { data } = await gQLMixin.methods.communicate(gqlQuery.GetMostViewedCompanies, { filter })
      commit(SET_MOST_VIEWED, data.getMostViewedCompanies.data)
      return data.getMostViewedCompanies.data
    } catch (err) {
      handleGraphQLErr(err)
    }
  },
  async getNextReportingList(
    { commit, rootGetters },
    { limit, allLicenses } = {
      limit: 5,
      allLicenses: false,
    },
  ) {
    try {
      const watchlistStore = useWatchlistsStore()
      const { activeWatchlists } = storeToRefs(watchlistStore)
      const { data } = await gQLMixin.methods.communicate(gqlQuery.GetCompanyListOrderedByNextReporting, {
        limit,
        filter: {
          licenseId: allLicenses ? [] : rootGetters['User/selectedUniverseIds'],
          retired: false,
          nextReportingIsInThePast: false,
          nextReportingMode: true,
          watchlists: activeWatchlists.value,
        },
      })
      commit(SET_NEXT_REPORTING_LIST, data.getCompanyListV2.data)
      return data.getCompanyListV2.data
    } catch (err) {
      handleGraphQLErr(err)
    }
  },

  async getLastUpdatedCompanies(
    { commit, rootGetters },

    { limit, allLicenses } = {
      limit: 5,
      allLicenses: false,
    },
  ) {
    try {
      const { data } = await gQLMixin.methods.communicate(gqlQuery.GetLastUpdatedCompanies, {
        limit,
        filter: {
          licenseId: allLicenses ? [] : rootGetters['User/selectedUniverseIds'],
          months: 2,
        },
      })

      commit(SET_LAST_UPDATED_LIST, data.lastUpdatedList.data)
      return data
    } catch (err) {
      handleGraphQLErr(err)
      return err
    }
  },
  /**
   * Get detailed data of the passed company and populate
   * the currentCompany value within the Company State
   * @param { Object } payload - the ID of the company e.g. { id: 'Adient' }
   */
  async getCompanyDetail({ commit }: ActionContext, payload) {
    try {
      const { response } = await useGraphQL(gqlQuery.GetCompanyDetail, payload)
      if (!response.data?.companyDetails) {
        throw new Error('The companyDetails object is missing from the returned data')
      }

      commit(CURRENT_COMPANY, response.data?.companyDetails?.company)
      return response.data.companyDetails
    } catch (err) {
      console.error('[ERROR] - The detailed data about the company did return something wrong', err.message)
      return err
    }
  },
  updateCurrentCompany({ commit }: ActionContext, payload) {
    commit(UPDATE_CURRENT_COMPANY, payload)
  },
  async getIndustryPeers(_, payload) {
    try {
      const res = await getCompanyCompsList({ id: payload })
      if (!Array.isArray(res.data)) throw new Error('[ERROR] - Unable to get the company comparables data')
      return res.data
    } catch (err) {
      console.error(err.message)
      return err
    }
  },
  async getCompanyInstruments(_, payload) {
    try {
      const { response } = await useGraphQL(gqlQuery.GetCompanyInstruments, payload)

      if (!response.data) {
        throw new Error('[ERROR] - Unable to get the instruments data')
      }

      return response.data?.getCompany?.company
    } catch (err) {
      console.error(err)
      return err
    }
  },
  async getMemoFinancials(_, payload) {
    try {
      const { response } = await useGraphQL(gqlQuery.GetCompanyMemoDatapoints, payload)
      return response.data?.getCompanyMemoDatapoints
    } catch (err) {
      return new Error(err)
    }
  },
  async getMemoStructure({ commit }: ActionContext, payload) {
    try {
      const { response } = await useGraphQL(gqlQuery.GetCompanyMemoStructure, payload)

      if (!response.data) {
        throw new Error('[ERROR] - Unable to get the structure data')
      }

      commit(SET_MEMO_STRUCTURE_LIST, response.data?.getCompanyMemoStructure?.companyMemoStructure)
      return response.data
    } catch (err) {
      console.error(err)
      return err
    }
  },
  /**
   * This is using the old API
   * It will eventually replaced by AppSync
   * @param { String } datapointId
   */
  async getDebugDatapoints(_, datapointId) {
    try {
      const { data } = await gQLMixin.methods.communicate(gqlQuery.GetDebugDatapoint, { dpId: datapointId })

      if (!data) {
        throw new Error('[ERROR] - Unable to get the debug information')
      }
      return data
    } catch (err) {
      return err
    }
  },
  /**
   * @param { String } datapointId
   */
  async getDatapointsInfo(_, datapointId) {
    try {
      const { data } = await gQLMixin.methods.communicate(gqlQuery.GetDatapointsInfo, { dpIds: [datapointId] })

      if (!data) {
        throw new Error('[ERROR] - Unable to get the debug information')
      }
      return data
    } catch (err) {
      return err
    }
  },
  /**
   * @param { Object } payload { batchId, batchType}
   */
  async getCompanyDatapointsByBatch(_, payload) {
    const { batchId, batchType, companyId, subset } = payload
    try {
      const res =
        batchType && batchType === 'ondemand'
          ? await gQLMixin.methods.communicate(gqlQuery.GetBatchMapLink, { batchId })
          : await gQLMixin.methods.communicate(gqlQuery.GetCompanyDataMapLink, { companyId, subset, batchId })

      if (!res) {
        throw new Error('[ERROR] - Unable to get the data map')
      }

      return res
    } catch (err) {
      return err
    }
  },

  /**
   *
   * @param { Object } payload - An Object containing the URL e.g. { url: https://mydomain.com/files/file.json }
   */
  async getCompanyDataGrid(_, payload) {
    try {
      const req = await instance()
      const { data } = await req.get(payload.url)

      if (!data) {
        throw new Error('[ERROR] - Unable to get the data grid')
      }

      return data
    } catch (err) {
      return err
    }
  },
  async getCompanyMemo(_, payload) {
    try {
      const { data } = await gQLMixin.methods.communicate(gqlQuery.GetCompanyMemo, payload)
      return data?.getCompany?.company
    } catch (err) {
      console.error('[ERROR] Fetching the company memo', err)
      return err
    }
  },
  async getCompanyForecastingParams(_, payload) {
    try {
      const { data } = await gQLMixin.methods.communicate(gqlQuery.GetCompanyForecastingParams, payload)
      return data?.getCompany?.company?.forecasting
    } catch (err) {
      console.error('[ERROR] Fetching the forecasting parameters', err)
      return err
    }
  },
  wipeCompanyDetail({ commit }) {
    commit(CURRENT_COMPANY, null)
  },
  async getCompanyComps(_, payload) {
    try {
      const { response } = await useGraphQL(gqlQuery.GetCompanyCompsList, payload)
      return response.data.getCompanyMemoCompsDatapoints
    } catch (err) {
      return err
    }
  },
  async getCashSourceList({ commit }) {
    try {
      const { data } = await gQLMixin.methods.communicate(gqlQuery.GetCashSourceList, {})
      commit(SET_CASH_SOURCE_LIST, data.getCashSourceList)
      return data.getCashSourceList
    } catch (err) {
      console.error(err)
      return err
    }
  },
  async getGicsList({ commit }) {
    try {
      const { data } = await gQLMixin.methods.communicate(gqlQuery.GetIndustryList, {})
      commit(SET_GICS_LIST, data.getIndustryList)
      return data.getIndustryList
    } catch (err) {
      handleGraphQLErr(err)
      return err
    }
  },
  async getUiInfo({ commit }: ActionContext, payload) {
    try {
      const { response } = await useGraphQL(gqlQuery.GetUiInfo, payload)
      const pageInfo = {
        descriptions: response.data.getUiInfo.descriptions ?? [],
        options: response.data.getUiInfo.options ?? [],
      }
      commit(GET_UI_INFO, { data: pageInfo, area: payload.page })
    } catch (err) {
      console.error('Error loading UI info', err)
    }
  },
  async setCompanyListPage({ commit }: ActionContext, payload) {
    commit(SET_COMPANY_LIST_PAGE, payload)
  },

  async getCompanyRecentDocuments({ commit }: ActionContext, payload) {
    try {
      const { data } = await gQLMixin.methods.communicate(gqlQuery.GetCompanyRecentlyAddedDocuments, {
        id: payload,
        months: 6,
      })
      const { recentDocuments } = data
      if (recentDocuments.error) {
        throw new Error(recentDocuments.error)
      } else {
        commit(SET_COMPANY_RECENT_DOCUMENTS, recentDocuments.data)
        return recentDocuments.data
      }
    } catch (err) {
      handleGraphQLErr(err)
      return err
    }
  },
  async getEarningsSummary({ commit }: ActionContext, payload) {
    try {
      const { data } = await gQLMixin.methods.communicate(gqlQuery.GetCompanyEarningsSummary, {
        companyId: payload,
      })
      if (data.earningsSummary.latestPeriod || data.earningsSummary.latestYearly) {
        commit(SET_EARNINGS_SUMMARY, data.earningsSummary)
        return data.earningsSummary
      } else {
        commit(SET_EARNINGS_SUMMARY, null)
        return null
      }
    } catch (err) {
      console.error('Error loading earning summary', err)
    }
  },
  async toggleWatchlistTooltip({ commit }: ActionContext, payload: string | undefined) {
    commit(SET_WATCHLIST_TOOLTIP, payload)
  },
  // these two actions are used to ensure the local state of the current
  // company is updated when adding/removing from watchlist
  addToWatchlist({ commit }: ActionContext, { companyId, watchlistId }) {
    commit(ADD_TO_WATCHLIST, { companyId, watchlistId })
  },
  removeFromWatchlist({ commit }: ActionContext, { companyId, watchlistId }) {
    commit(REMOVE_FROM_WATCHLIST, { companyId, watchlistId })
  },
}

export type CompanyActions = typeof companyActions
export default companyActions
